// SB6-ANA-POP-02 (Phase 3) — window-centric pop-out workspace store.
//
// Supersedes Phase-1's widget-centric registry ("one widget = one window").
// Now a *window* holds a SET of widgets arranged in its own grid, and there
// can be up to MAX_WINDOWS (Christian Q3 = 3). A widget lives in exactly one
// place: the main dashboard (absent from every window) OR one pop-out window.
//
// State is session-only (Q4 = a) and shared across all open windows by a
// last-write-wins broadcast (see `useWorkspaceSync`). This module is the pure
// state container + reducer; the channel wiring lives in the hook so the
// reducer stays unit-testable without BroadcastChannel.
import { create } from "zustand";
import type { PopoutWidgetKey } from "./windowSizes.js";

export const MAX_POPOUT_WINDOWS = 3;

/** Pop-out grid is a 12-col react-grid-layout, row height 30 (matches the
 *  main analysis workspace). */
export const POPOUT_GRID_COLS = 12;

export interface PopoutGridCell {
  /** Widget key. */
  readonly i: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface PopoutWindowState {
  readonly windowId: string;
  /** RGL layout; membership of the window = the set of `i`s present here. */
  readonly layout: readonly PopoutGridCell[];
  /** Best-effort screen the window was placed on (session hint). */
  readonly screenLabel?: string;
  readonly openedAt: number;
}

/** Default cell size per widget on the 12-col pop-out grid. Unknown / future
 *  widgets fall back to a sensible medium tile. */
const DEFAULT_CELL: Partial<Record<PopoutWidgetKey, { w: number; h: number }>> =
  {
    viewer: { w: 8, h: 14 },
    tiles: { w: 4, h: 8 },
    list: { w: 4, h: 14 },
    sketch: { w: 3, h: 14 },
    filmstrip: { w: 3, h: 14 },
    navigator: { w: 3, h: 8 },
    catalogue: { w: 3, h: 8 },
    // SB6-ANA-POP-04 (D3) — tall filter tables; wider than the w:4 rec.
    aiFilter: { w: 5, h: 10 },
    defectFilter: { w: 5, h: 10 },
  };

function cellFor(widget: PopoutWidgetKey): { w: number; h: number } {
  return DEFAULT_CELL[widget] ?? { w: 4, h: 8 };
}

/** Append a widget below the existing stack of a window's grid, full default
 *  span, clamped to the column count. Pure. */
export function appendCell(
  layout: readonly PopoutGridCell[],
  widget: PopoutWidgetKey,
): PopoutGridCell {
  const def = cellFor(widget);
  const bottom = layout.reduce((acc, l) => Math.max(acc, l.y + l.h), 0);
  return {
    i: widget,
    x: 0,
    y: bottom,
    w: Math.min(def.w, POPOUT_GRID_COLS),
    h: def.h,
  };
}

/** Widgets currently in any window (i.e. NOT on the main dashboard). */
export function outWidgetsOf(
  windows: readonly PopoutWindowState[],
): Set<PopoutWidgetKey> {
  const out = new Set<PopoutWidgetKey>();
  for (const w of windows) {
    for (const cell of w.layout) out.add(cell.i as PopoutWidgetKey);
  }
  return out;
}

/** The windowId currently holding `widget`, or null if it's on the main grid. */
export function windowOf(
  windows: readonly PopoutWindowState[],
  widget: PopoutWidgetKey,
): string | null {
  for (const w of windows) {
    if (w.layout.some((c) => c.i === widget)) return w.windowId;
  }
  return null;
}

// --- pure reducers (exported for unit tests) -------------------------------

/** Remove a widget from whichever window holds it; drop now-empty windows. */
export function removeWidget(
  windows: readonly PopoutWindowState[],
  widget: PopoutWidgetKey,
): PopoutWindowState[] {
  return windows
    .map((w) => ({ ...w, layout: w.layout.filter((c) => c.i !== widget) }))
    .filter((w) => w.layout.length > 0);
}

/** Add a widget to an existing window (appended below its stack), after first
 *  removing it from any window it was already in. */
export function addWidgetToWindow(
  windows: readonly PopoutWindowState[],
  widget: PopoutWidgetKey,
  windowId: string,
): PopoutWindowState[] {
  const pruned = removeWidget(windows, widget);
  return pruned.map((w) =>
    w.windowId === windowId
      ? { ...w, layout: [...w.layout, appendCell(w.layout, widget)] }
      : w,
  );
}

interface WorkspaceState {
  readonly userId: string | null;
  /** Monotonic-ish clock for last-write-wins reconciliation (Date.now ms). */
  readonly version: number;
  /** `local` = a mutation made here (broadcast it); `remote` = applied from
   *  another window (do NOT re-broadcast); `init` = hydrate/clear. */
  readonly origin: "local" | "remote" | "init";
  readonly windows: readonly PopoutWindowState[];

  readonly hydrate: (userId: string) => void;
  /** Open a new window seeded with `widget`. Returns the new windowId, or
   *  null if already at MAX_POPOUT_WINDOWS. */
  readonly openWindowWith: (
    widget: PopoutWidgetKey,
    screenLabel?: string,
  ) => string | null;
  /** Move `widget` into an existing window. */
  readonly assignToWindow: (widget: PopoutWidgetKey, windowId: string) => void;
  /** Send `widget` back to the main grid (closes its window if it empties). */
  readonly unassign: (widget: PopoutWidgetKey) => void;
  /** Close a window — all its widgets return to the main grid. */
  readonly closeWindow: (windowId: string) => void;
  /** Replace a window's grid layout (drag/resize inside the pop-out). */
  readonly setWindowLayout: (
    windowId: string,
    layout: readonly PopoutGridCell[],
  ) => void;
  /** Apply state received from another window (no re-broadcast). */
  readonly applyRemote: (
    version: number,
    windows: readonly PopoutWindowState[],
  ) => void;
  readonly clear: () => void;
}

function storageKey(userId: string): string {
  return `aero.lyse.popout.workspace.${userId}`;
}

function loadFromSession(userId: string): readonly PopoutWindowState[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (w): w is PopoutWindowState =>
        typeof w === "object" &&
        w !== null &&
        typeof (w as { windowId?: unknown }).windowId === "string" &&
        Array.isArray((w as { layout?: unknown }).layout),
    );
  } catch {
    return [];
  }
}

function persist(userId: string, windows: readonly PopoutWindowState[]): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(storageKey(userId), JSON.stringify(windows));
  } catch {
    /* quota / disabled — best effort */
  }
}

/** Make-believe v4 — sufficient as an id, not for crypto. */
function uuid(): string {
  return (
    "w-" +
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 8)
  );
}

export const usePopoutWorkspaceStore = create<WorkspaceState>((set, get) => {
  const commit = (windows: readonly PopoutWindowState[]): void => {
    const userId = get().userId;
    if (userId) persist(userId, windows);
    set({ windows, version: Date.now(), origin: "local" });
  };

  return {
    userId: null,
    version: 0,
    origin: "init",
    windows: [],

    hydrate: (userId: string): void => {
      if (get().userId === userId) return;
      set({
        userId,
        windows: loadFromSession(userId),
        version: Date.now(),
        origin: "init",
      });
    },

    openWindowWith: (widget, screenLabel): string | null => {
      const { windows } = get();
      if (windows.length >= MAX_POPOUT_WINDOWS) return null;
      const windowId = uuid();
      const pruned = removeWidget(windows, widget);
      const newWindow: PopoutWindowState = {
        windowId,
        layout: [appendCell([], widget)],
        openedAt: Date.now(),
        ...(screenLabel ? { screenLabel } : {}),
      };
      commit([...pruned, newWindow]);
      return windowId;
    },

    assignToWindow: (widget, windowId): void => {
      commit(addWidgetToWindow(get().windows, widget, windowId));
    },

    unassign: (widget): void => {
      commit(removeWidget(get().windows, widget));
    },

    closeWindow: (windowId): void => {
      commit(get().windows.filter((w) => w.windowId !== windowId));
    },

    setWindowLayout: (windowId, layout): void => {
      commit(
        get().windows.map((w) =>
          w.windowId === windowId ? { ...w, layout: [...layout] } : w,
        ),
      );
    },

    applyRemote: (version, windows): void => {
      if (version <= get().version) return;
      const userId = get().userId;
      if (userId) persist(userId, windows);
      set({ windows, version, origin: "remote" });
    },

    clear: (): void => {
      const userId = get().userId;
      if (userId) persist(userId, []);
      set({ windows: [], version: Date.now(), origin: "init" });
    },
  };
});

/** Test bridge — surface the workspace store at `window.__aeroLyseWorkspace`
 *  so the acceptance spec can drive windows without real pop-up windows. */
export function mountWorkspaceBridge(): () => void {
  if (typeof window === "undefined") return () => undefined;
  interface Bridge {
    windows: readonly PopoutWindowState[];
    openWindowWith: (
      widget: PopoutWidgetKey,
      screenLabel?: string,
    ) => string | null;
    assignToWindow: (widget: PopoutWidgetKey, windowId: string) => void;
    unassign: (widget: PopoutWidgetKey) => void;
    closeWindow: (windowId: string) => void;
  }
  const expose = (): void => {
    const s = usePopoutWorkspaceStore.getState();
    (
      window as unknown as { __aeroLyseWorkspace?: Bridge }
    ).__aeroLyseWorkspace = {
      windows: s.windows,
      openWindowWith: s.openWindowWith,
      assignToWindow: s.assignToWindow,
      unassign: s.unassign,
      closeWindow: s.closeWindow,
    };
  };
  const unsub = usePopoutWorkspaceStore.subscribe(expose);
  expose();
  return () => {
    unsub();
    delete (window as unknown as { __aeroLyseWorkspace?: Bridge })
      .__aeroLyseWorkspace;
  };
}
